/**
 * Documents/Attachments slice - single source for documents API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { ATTACHMENTS } from '../../api/endpoints';

export const getAttachments = createAsyncThunk(
  'documents/getAttachments',
  async ({ modelType, modelId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(ATTACHMENTS.LIST, {
        params: { model_type: modelType, model_id: modelId },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch attachments');
    }
  }
);

export const downloadAttachment = createAsyncThunk(
  'documents/downloadAttachment',
  async (attachmentId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/attachments/${attachmentId}/download`, {
        responseType: 'blob',
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to download file');
    }
  }
);

export const deleteAttachment = createAsyncThunk(
  'documents/deleteAttachment',
  async (attachmentId, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(ATTACHMENTS.DOWNLOAD(attachmentId).replace('/download', ''));
      return { ...data, attachmentId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete file');
    }
  }
);

export const downloadLeadFile = createAsyncThunk(
  'documents/downloadLeadFile',
  async (path, { rejectWithValue }) => {
    try {
      const response = await api.post(ATTACHMENTS.DOWNLOAD_BY_PATH, { path }, { responseType: 'blob' });
      return { data: response.data, headers: response.headers };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Download failed');
    }
  }
);

const initialState = {
  attachments: null,
  loading: false,
  error: null,
};

const documentsSlice = createSlice({
  name: 'documents',
  initialState,
  reducers: {
    clearAttachments: (state) => {
      state.attachments = null;
    },
    clearDocumentsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.attachments = action.payload;
      })
      .addCase(getAttachments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong';
      })
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        if (state.attachments?.list && action.payload?.attachmentId) {
          state.attachments.list = state.attachments.list.filter(
            (a) => a.id !== action.payload.attachmentId
          );
        }
      });
  },
});

export const { clearAttachments, clearDocumentsError } = documentsSlice.actions;
export default documentsSlice.reducer;
